import { COMPANY_TYPE, defaultUomMapping, preferredMeasurementSystemMapping } from "../constants"

import { type CompanyPreferredMeasurementSystemEnum, CompanyStatus } from "@/graphql/codegen/graphql"
import { CUSTOMER_COMPANY_TYPES, CompanyType, VENDOR_COMPANY_TYPES } from "@/types/companyType"
import type { TreeNode } from "@/types/tree"

export function formatMeasurementSystemUoM(
  preferredMeasurementSystem: CompanyPreferredMeasurementSystemEnum,
  defaultUom: string | undefined
): string {
  return `${preferredMeasurementSystemMapping[preferredMeasurementSystem]} / ${defaultUom ? defaultUomMapping[defaultUom as keyof typeof defaultUomMapping] : "-"}`
}

export const findCompanyType = (nodes: TreeNode<string>[], targetId: string): TreeNode<string> | undefined => {
  for (const node of nodes) {
    if (node.id === targetId) return node
    if (node.children) {
      const found = findCompanyType(node.children, targetId)
      if (found) return found
    }
  }
  return undefined
}

type CompanyStatusColor = "success" | "error" | "warning"

export const getCompanyStatusColor = (status: CompanyStatus): CompanyStatusColor => {
  switch (status) {
    case CompanyStatus.Active:
      return "success"
    case CompanyStatus.Inactive:
      return "error"
    default:
      return "warning"
  }
}

// Return true if you need disable category
export const validationCategoryCompanyType = (
  companyField: { value: string[]; onChange: (value: string[]) => void },
  companyNode: TreeNode<string>
): boolean => {
  const fieldValue = companyField?.value
  const isCustomerCompany = hasMatchingCompanyTypes(fieldValue, CUSTOMER_COMPANY_TYPES)

  if (isCustomerCompany && (companyNode.id === CompanyType.Vendor || companyNode.parent_id === CompanyType.Vendor)) {
    return true
  }

  const isVendorCompany = hasMatchingCompanyTypes(fieldValue, VENDOR_COMPANY_TYPES)

  if (
    isVendorCompany &&
    (companyNode.id === COMPANY_TYPE.CUSTOMER || companyNode.parent_id === COMPANY_TYPE.CUSTOMER)
  ) {
    return true
  }

  return false
}

export const validationCompanyType = (
  companyField: { value: string[]; onChange: (value: string[]) => void },
  companyNode: TreeNode<string>,
  disableCategory?: boolean
): boolean => {
  const fieldValue = companyField?.value
  const isCustomerCompany = hasMatchingCompanyTypes(fieldValue, CUSTOMER_COMPANY_TYPES)
  const isPurchaserCompanySelected = hasMatchingCompanyTypes(fieldValue, [CompanyType.Purchaser])

  if (disableCategory) {
    return true
  }

  if (
    isPurchaserCompanySelected &&
    CUSTOMER_COMPANY_TYPES.includes(companyNode.id as CompanyType) &&
    companyNode.id !== CompanyType.Purchaser
  ) {
    return true
  }

  if (
    isCustomerCompany &&
    !isPurchaserCompanySelected &&
    CUSTOMER_COMPANY_TYPES.includes(companyNode.id as CompanyType) &&
    companyNode.id === CompanyType.Purchaser
  ) {
    return true
  }

  return false
}

const hasMatchingCompanyTypes = (fieldValue: string[], companyTypes: readonly CompanyType[]) =>
  fieldValue?.some((item) => companyTypes.includes(item as CompanyType))

// AK-6592: The backend names the input that failed validation. Anything it does not name is shown on the name input.
export const toCompanyFormErrorField = (field: string | null | undefined): "name" | "description" =>
  field === "description" ? "description" : "name"
